import React, { useContext, useEffect, useState } from 'react';
import CommonTable from '../../commonComponents/Table/table';
import {
    BgColorsOutlined,
    CheckCircleOutlined,
    DownOutlined,
    EditOutlined,
    FormOutlined,
    KeyOutlined,
    SafetyOutlined,
} from '@ant-design/icons';
import { Button, Divider, notification, Pagination, Popover, Spin, Switch, Tag, Tooltip } from 'antd';
import { fetchAllCompanies, fetchCompaniesDetailId, useCompanies, useEditCompanies, useShareCredentials, useMarkCompanyAsGTOM, useMarkCompanyAsAutoDDApproval } from '../../hooks/useCompanies';
import DynamicModal from '../../commonComponents/DynamicModal/DynamicModal';
import NewClient from '../newClientOnboard/newClient';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ToasterContext } from '../../context/toasterContext';
import useLogout from '../../hooks/useLogout';
import CSVConfig from '../../components/CSVConfig';
import EditRenamingModal from '../../components/EditRenamingModal';
import SpinLoader from '../../commonComponents/SpinLoader';
import PlusIconActionButton from '../../components/PlusIconActionButton';
import Swatch from '../../pages/swatches';
import StyleColorLibraryModal from '../../components/StyleColorLibraryModal';
import ManagePIIKeysModal from '../../components/ManagePIIKeysModal';
import ProductVariantFieldModal from '../../components/ProductVariantFieldModal';
import palette from '../../assets/images/palette.png';
import csvconfig from '../../assets/images/config.png';
import stc from 'string-to-color';
import 'react-multi-email/dist/style.css';
import CredentialForm from '../../components/credentialForm';
import DebounceInput from '../../commonComponents/DebounceInput';
import styles from './allClients.module.css';

const AllClients = () => {
    const [editFormFlag, setEditFormFlag] = useState(false);
    const [openCSVConfigModal, setOpenCSVConfigModal] = useState(false);
    const [openSwatchCreationModal, setOpenSwatchCreationModal] = useState(false);
    const [openRenamingModal, setOpenRenamingModal] = useState(false);
    const [openStyleColorLibraryModal, setOpenStyleColorLibraryModal] = useState(false);
    const [openPIIKeysModal, setOpenPIIKeysModal] = useState(false);
    const [openProductVariantFieldModal, setOpenProductVariantFieldModal] = useState(false);
    const [selectedCompanyForPII, setSelectedCompanyForPII] = useState(null);
    const [selectedCompanyForVariantField, setSelectedCompanyForVariantField] = useState(null);
    const [selectedRecordForLibrary, setSelectedRecordForLibrary] = useState(null);
    const [companyId, setCompanyId] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [address, setAddresses] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [brands, setBrands] = useState([]);
    const [clientName, setClientName] = useState('');
    const [api, contextHolder] = notification.useNotification();
    const { toastFlag } = useContext(ToasterContext);
    const [localToaster, setLocalToaster] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const { logout } = useLogout();
    const [toasterShow, setToasterShow] = toastFlag;
    const { state } = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [credentialData, setCredentialData] = useState({
        flag: false,
        data: {},
        emails: [],
    });

    const [searchText, setSearchText] = useState(null);
    const [gtomByCompanyCode, setGtomByCompanyCode] = useState({});
    const [gtomLoadingCode, setGtomLoadingCode] = useState(null);
    const [autoDDByCompanyCode, setAutoDDByCompanyCode] = useState({});
    const [autoDDLoadingCode, setAutoDDLoadingCode] = useState(null);

    let pageSize = 10;

    const {
        refetch: getFetchAllCompanies,
        data: companies,
        isLoading,
        isError,
        isFetching,
    } = useQuery(['company', pageNumber, pageSize, searchText], fetchAllCompanies, {
        enabled: true,
    });

    const getData = () => fetchCompaniesDetailId(companyId);

    const { refetch, data: editDetailsData } = useQuery(['companyDetails', companyId], getData, {
        enabled: false,
        cacheTime: 0,
    });
    const onSuccessShare = () => {
        api.success({
            message: 'Request successful',
        });
        closeCredential();
    };
    const onErrorShare = () => {
        closeCredential();
        api.error({
            message: 'Request Failed',
            description: 'Try again after sometime',
        });
    };
    const { mutate: shareCredentials } = useShareCredentials(onSuccessShare, onErrorShare);

    const getCompanyCode = record => record?.code;

    const getGtomChecked = record => {
        const companyCode = getCompanyCode(record);
        if (!companyCode) return false;
        if (Object.prototype.hasOwnProperty.call(gtomByCompanyCode, companyCode)) {
            return gtomByCompanyCode[companyCode];
        }
        return record?.isGTOM ?? false;
    };

    const getAutoDDChecked = record => {
        const companyCode = getCompanyCode(record);
        if (!companyCode) return false;
        if (Object.prototype.hasOwnProperty.call(autoDDByCompanyCode, companyCode)) {
            return autoDDByCompanyCode[companyCode];
        }
        return record?.isAutoDDApprovalEnabled ?? false;
    };

    const onSuccessMarkGTOM = updatedCompany => {
        const companyCode = updatedCompany?.code;
        const isGTOM = updatedCompany?.isGTOM ?? false;

        if (companyCode) {
            setGtomByCompanyCode(prev => ({
                ...prev,
                [companyCode]: isGTOM,
            }));

            queryClient.setQueryData(['company', pageNumber, pageSize, searchText], oldData => {
                if (!oldData?.data?.data) return oldData;

                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        data: oldData.data.data.map(company =>
                            company.code === companyCode ? { ...company, isGTOM } : company
                        ),
                    },
                };
            });
        }

        setGtomLoadingCode(null);
        api.success({
            message: `GTOM ${isGTOM ? 'enabled' : 'disabled'} for ${updatedCompany?.name || companyCode}`,
        });
    };

    const onErrorMarkGTOM = (error, variables) => {
        setGtomByCompanyCode(prev => ({
            ...prev,
            [variables.companyCode]: !variables.isGTOM,
        }));
        setGtomLoadingCode(null);
        api.error({
            message: 'Failed to update GTOM',
            description: error?.response?.data?.message || 'Try again after sometime',
        });
    };

    const { mutate: markCompanyAsGTOM } = useMarkCompanyAsGTOM(onSuccessMarkGTOM, onErrorMarkGTOM);

    const onSuccessMarkAutoDD = updatedCompany => {
        const companyCode = updatedCompany?.code;
        const isAutoDDApprovalEnabled = updatedCompany?.isAutoDDApprovalEnabled ?? false;

        if (companyCode) {
            setAutoDDByCompanyCode(prev => ({
                ...prev,
                [companyCode]: isAutoDDApprovalEnabled,
            }));

            queryClient.setQueryData(['company', pageNumber, pageSize, searchText], oldData => {
                if (!oldData?.data?.data) return oldData;

                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        data: oldData.data.data.map(company =>
                            company.code === companyCode ? { ...company, isAutoDDApprovalEnabled } : company
                        ),
                    },
                };
            });
        }

        setAutoDDLoadingCode(null);
        api.success({
            message: `Auto DD Approval ${isAutoDDApprovalEnabled ? 'enabled' : 'disabled'} for ${updatedCompany?.name || companyCode}`,
        });
    };

    const onErrorMarkAutoDD = (error, variables) => {
        setAutoDDByCompanyCode(prev => ({
            ...prev,
            [variables.companyCode]: !variables.isAutoDDApprovalEnabled,
        }));
        setAutoDDLoadingCode(null);
        api.error({
            message: 'Failed to update Auto DD Approval',
            description: error?.response?.data?.message || 'Try again after sometime',
        });
    };

    const { mutate: markCompanyAsAutoDDApproval } = useMarkCompanyAsAutoDDApproval(onSuccessMarkAutoDD, onErrorMarkAutoDD);
    useEffect(() => {
        if (toasterShow) {
            api.success({
                message: 'Company created successfully',
            });
            setToasterShow(false);
        }
    }, []);

    useEffect(() => {
        if (localToaster) {
            api.success({
                message: `Successful!`,
                placement: 'topRight',
            });
            setLocalToaster(false);
        }
    }, [localToaster]);

    useEffect(() => {
        if (companyId) {
            refetch();
        }
        getFetchAllCompanies();
    }, [companyId]);

    useEffect(() => {
        setAddresses(editDetailsData?.data?.addresses ?? []);
        setBrands(editDetailsData?.data?.brands ?? []);
        setContacts(editDetailsData?.data?.contactDetails ?? []);
    }, [editDetailsData]);

    const onEditCompanyDetails = data => {
        setEditFormFlag(true);
        setCompanyId(data.key);
    };

    const openCSVConfig = record => {
        setClientName(record.name);
        setCompanyId(record.key);
        setOpenCSVConfigModal(true);
    };

    const handleSwatchCreation = record => {
        setClientName(record.name);
        setCompanyId(record.key);
        setCompanyCode(record.code);
        setOpenSwatchCreationModal(true);
    };

    const OnConfigModalClose = () => {
        setCompanyId('');
        setOpenCSVConfigModal(false);
    };
    const OnSwatchCreationModalClose = () => {
        setCompanyId('');
        setOpenSwatchCreationModal(false);
    };

    const onEditRenaming = record => {
        setCompanyId(record.key);
        setCompanyName(record.name);
        setOpenRenamingModal(true);
    };
    const onCloseRenamingModal = () => {
        setOpenRenamingModal(false);
        setCompanyId('');
    };
    const handleStyleColorLibrary = record => {
        setSelectedRecordForLibrary(record);
        setOpenStyleColorLibraryModal(true);
    };
    const onCloseStyleColorLibraryModal = () => {
        setOpenStyleColorLibraryModal(false);
        setSelectedRecordForLibrary(null);
    };
    const handleManagePIIKeys = record => {
        setSelectedCompanyForPII(record);
        setOpenPIIKeysModal(true);
    };
    const onClosePIIKeysModal = () => {
        setOpenPIIKeysModal(false);
        setSelectedCompanyForPII(null);
    };
    const handleManageProductVariantField = record => {
        setSelectedCompanyForVariantField(record);
        setOpenProductVariantFieldModal(true);
    };
    const onCloseProductVariantFieldModal = () => {
        setOpenProductVariantFieldModal(false);
        setSelectedCompanyForVariantField(null);
    };
    const closeCredential = () => {
        setCredentialData({ flag: false, data: {}, emails: [] });
    };

    const requestCredentials = () => {
        if (credentialData.emails.length > 0) {
            shareCredentials({
                id: credentialData.data.key,
                emails: credentialData.emails,
            });
        } else {
            api.warning({
                message: 'Wrong Request',
                description: 'Please fill recipient email',
            });
        }
    };

    const handleGtomToggle = (record, isGTOM) => {
        const companyCode = getCompanyCode(record);
        if (!companyCode) {
            api.error({
                message: 'Failed to update GTOM',
                description: 'Company code not found for this client',
            });
            return;
        }

        setGtomByCompanyCode(prev => ({
            ...prev,
            [companyCode]: isGTOM,
        }));
        setGtomLoadingCode(companyCode);
        markCompanyAsGTOM({
            companyCode,
            isGTOM,
        });
    };

    const handleAutoDDToggle = (record, isAutoDDApprovalEnabled) => {
        const companyCode = getCompanyCode(record);
        if (!companyCode) {
            api.error({
                message: 'Failed to update Auto DD Approval',
                description: 'Company code not found for this client',
            });
            return;
        }

        setAutoDDByCompanyCode(prev => ({
            ...prev,
            [companyCode]: isAutoDDApprovalEnabled,
        }));
        setAutoDDLoadingCode(companyCode);
        markCompanyAsAutoDDApproval({
            companyCode,
            isAutoDDApprovalEnabled,
        });
    };

    const columns = [
        {
            title: 'Client Name',
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexDirection: 'row',
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: stc(record?.code),
                                width: '10px',
                                height: '50px',
                                // border: "1px solid #000",
                                marginRight: '15px',
                            }}
                        ></div>
                        <span>
                            <b>{record?.name}</b>
                        </span>
                    </div>
                );
            },
        },
        {
            title: 'Client Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Brands',
            dataIndex: 'brands',
            key: 'brands',
            render: (_, record) => <span>{record.brands && record.brands.join(', ')}</span>,
        },
        {
            title: 'Preferred Studio',
            key: 'studio',
            dataIndex: 'preferredStudios',
        },
        {
            title: 'Enabled Features',
            key: 'features',
            width: 180,
            render: (_, record) => {
                const gtomOn = getGtomChecked(record);
                const autoDDOn = getAutoDDChecked(record);
                if (!gtomOn && !autoDDOn) {
                    return <span style={{ color: 'rgba(0,0,0,0.25)' }}>—</span>;
                }
                return (
                    <div className={styles.featureTags}>
                        {gtomOn && (
                            <Tag color="blue" icon={<CheckCircleOutlined />}>
                                GTOM
                            </Tag>
                        )}
                        {autoDDOn && (
                            <Tag color="green" icon={<CheckCircleOutlined />}>
                                Auto DD
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'action',
            width: 180,
            render: (_, record) => {
                const gtomOn = getGtomChecked(record);
                const autoDDOn = getAutoDDChecked(record);
                const companyCode = getCompanyCode(record);

                const manageContent = (
                    <div className={styles.managePanel} onClick={e => e.stopPropagation()}>
                        <div className={styles.manageSection}>
                            <div className={styles.manageSectionTitle}>Features</div>
                            <div className={styles.manageRow}>
                                <div className={styles.manageRowLabel}>
                                    <span>
                                        GTOM
                                        <span className={styles.manageRowHint}>Ghost To OnModel</span>
                                    </span>
                                </div>
                                <Switch
                                    size="small"
                                    checked={gtomOn}
                                    loading={gtomLoadingCode === companyCode}
                                    onChange={checked => handleGtomToggle(record, checked)}
                                />
                            </div>
                            <div className={styles.manageRow}>
                                <div className={styles.manageRowLabel}>
                                    <span>
                                        Auto DD Approval
                                        <span className={styles.manageRowHint}>Auto-approve deliverable deck</span>
                                    </span>
                                </div>
                                <Switch
                                    size="small"
                                    checked={autoDDOn}
                                    loading={autoDDLoadingCode === companyCode}
                                    onChange={checked => handleAutoDDToggle(record, checked)}
                                />
                            </div>
                        </div>

                        <Divider className={styles.manageDivider} />

                        <div className={styles.manageSection}>
                            <div className={styles.manageSectionTitle}>Configuration</div>
                            <Button type="text" className={styles.manageActionBtn} onClick={() => openCSVConfig(record)}>
                                <img src={csvconfig} alt="" />
                                CSV Config
                            </Button>
                            <Button type="text" className={styles.manageActionBtn} onClick={() => onEditRenaming(record)}>
                                <FormOutlined />
                                Output Renaming
                            </Button>
                            <Button
                                type="text"
                                className={styles.manageActionBtn}
                                onClick={() => handleStyleColorLibrary(record)}
                            >
                                <BgColorsOutlined />
                                Style & Color Library
                            </Button>
                            <Button
                                type="text"
                                className={styles.manageActionBtn}
                                onClick={() => handleManagePIIKeys(record)}
                            >
                                <SafetyOutlined />
                                PII Keys
                            </Button>
                            <Button
                                type="text"
                                className={styles.manageActionBtn}
                                onClick={() => handleManageProductVariantField(record)}
                            >
                                <FormOutlined />
                                Product Variant Field
                            </Button>
                        </div>

                        <Divider className={styles.manageDivider} />

                        <div className={styles.manageSection}>
                            <div className={styles.manageSectionTitle}>Assets & Access</div>
                            <Button
                                type="text"
                                className={styles.manageActionBtn}
                                onClick={() => handleSwatchCreation(record)}
                            >
                                <img src={palette} alt="" />
                                Client Swatches
                            </Button>
                            <Button
                                type="text"
                                className={styles.manageActionBtn}
                                onClick={() =>
                                    setCredentialData({
                                        flag: true,
                                        data: record,
                                        emails: [atob(localStorage.getItem('email'))],
                                    })
                                }
                            >
                                <KeyOutlined />
                                SFTP Credentials
                            </Button>
                        </div>
                    </div>
                );

                return (
                    <div className={styles.clientActions} onClick={e => e.stopPropagation()}>
                        <Tooltip title="Edit company details">
                            <Button size="small" icon={<EditOutlined />} onClick={() => onEditCompanyDetails(record)}>
                                Edit
                            </Button>
                        </Tooltip>

                        <Popover
                            content={manageContent}
                            title={record?.name || 'Manage client'}
                            trigger="click"
                            placement="bottomRight"
                        >
                            <Button size="small">
                                Manage <DownOutlined style={{ fontSize: 10 }} />
                            </Button>
                        </Popover>
                    </div>
                );
            },
        },
    ];

    // if (isLoading) {
    //   return <SpinLoader />;
    // }
    if (isError) {
        logout();
    }
    const spinIcon = (
        <div className="spin-center">
            <Spin className="spin_loader" size="large" />{' '}
        </div>
    );

    const fetchNextData = pageNum => {
        let pageNo = pageNum++;
        setPageNumber(pageNo);
    };
    return (
        <>
            {contextHolder}
            {/* <ToastContainer/> */}
            {/* {companies?.data && ( */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    gap: '20px',
                    alignItems: 'center',
                }}
            >
                <span>Search by company</span>
                <div
                    style={{
                        maxWidth: '200px',
                        alignSelf: 'flex-end',
                    }}
                >
                    <DebounceInput
                        allowClear={true}
                        placeholder={'Search Companies'}
                        searchName={searchText}
                        setSearchName={data => {
                            setSearchText(data);
                            setPageNumber(1);
                        }}
                    />
                </div>
            </div>
            <div
                className="allClient-main"
                style={{
                    height: 'calc(100% - 110px)',
                    marginTop: '60px',
                    overflow: 'auto',
                    // border: '1px solid red',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                }}
            >
                <CommonTable
                    columns={columns}
                    data={companies?.data?.data}
                    bordered
                    loading={{
                        spinning: isFetching || isLoading,
                        indicator: spinIcon,
                    }}
                // pagination={{
                // 	position: ['bottomCenter']
                // }}
                />
            </div>
            {/* )} */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '1rem',
                    height: '50px',
                }}
            >
                <Pagination current={pageNumber} total={companies?.data?.totalCount} onChange={page => fetchNextData(page)} showSizeChanger={false} />
            </div>
            <PlusIconActionButton onClick={() => navigate('/onboardNewClient')} title={'Add Company'} />
            {editFormFlag && (
                <DynamicModal
                    open={true}
                    width={1000}
                    component={
                        <NewClient
                            companyId={companyId}
                            editAddress={editDetailsData?.data?.addresses}
                            editContact={editDetailsData?.data?.contactDetails}
                            editBrand={editDetailsData?.data?.brands}
                            editPreferStudio={editDetailsData?.data?.preferredStudios}
                            name={editDetailsData?.data?.name}
                            code={editDetailsData?.data?.code}
                            lastBatchNo={editDetailsData?.data?.lastBatchNo}
                            setEditFormFlag={setEditFormFlag}
                            setAddresses={setAddresses}
                            preferStudioName={editDetailsData?.data?.preferredStudios?.[0].name}
                            phoneNumberPrefix={editDetailsData?.data?.contactDetails?.[0]?.phoneNumber?.countryCode}
                            setBrands={setBrands}
                            getFetchAllCompanies={getFetchAllCompanies}
                            setCompanyId={setCompanyId}
                            setLocalToaster={setLocalToaster}
                            editFmsConfig={editDetailsData?.data?.fmsConfigOptions}
                        // fetchCompanies={fetchCompanies}
                        />
                    }
                />
            )}
            {openCSVConfigModal && (
                <DynamicModal
                    open={true}
                    width={1000}
                    closable={true}
                    close={OnConfigModalClose}
                    component={<CSVConfig clientName={clientName} companyId={companyId} />}
                />
            )}
            {openSwatchCreationModal && (
                <DynamicModal
                    open={true}
                    width={1000}
                    closable={true}
                    close={OnSwatchCreationModalClose}
                    component={<Swatch company={companyId} companyCode={companyCode} />}
                />
            )}
            {openRenamingModal && (
                <DynamicModal
                    open={true}
                    width="80%"
                    // height="100vh"
                    closable={true}
                    close={onCloseRenamingModal}
                    component={<EditRenamingModal companyId={companyId} companyName={companyName} />}
                />
            )}
            {credentialData.flag && (
                <DynamicModal
                    open={credentialData.flag}
                    width={600}
                    closable={true}
                    close={closeCredential}
                    component={
                        <CredentialForm
                            credentialData={credentialData}
                            setCredentialData={setCredentialData}
                            requestCredentials={requestCredentials}
                            toast={api}
                        />
                    }
                />
            )}
            {openStyleColorLibraryModal && (
                <DynamicModal
                    open={true}
                    width={800}
                    title="Style and Color Library"
                    closable={true}
                    close={onCloseStyleColorLibraryModal}
                    component={<StyleColorLibraryModal record={selectedRecordForLibrary} onClose={onCloseStyleColorLibraryModal} />}
                />
            )}
            <ManagePIIKeysModal
                open={openPIIKeysModal}
                companyId={selectedCompanyForPII?.key}
                companyName={selectedCompanyForPII?.name}
                onClose={onClosePIIKeysModal}
            />
            <ProductVariantFieldModal
                open={openProductVariantFieldModal}
                companyId={selectedCompanyForVariantField?.key}
                companyName={selectedCompanyForVariantField?.name}
                onClose={onCloseProductVariantFieldModal}
            />
        </>
    );
};
export default AllClients;
